<template>
  <div>
    <div class="flex items-center">
      <span class="mr-1">{{ props.title || 'Contact' }}</span>

      <IconAddressBook
        class="hover:bg-base-300 hover:cursor-pointer rounded-full p-1 inline-block"
        :width="30"
        :height="30"
        @click="openContactBook"
      />

      <button
        v-if="isMainContactForm"
        type="button"
        class="ml-2 bg-neutral hover:bg-neutral-focus text-white rounded px-2 py-1 text-sm"
        @click.prevent.stop="addMeToSign"
      >
        Add me to sign
      </button>
    </div>

    <!-- Select dropdown for contact -->
    <select
      v-model="bccSelectedContact"
      class="input input-sm input-bordered w-full mt-1"
      required
      @change="onBccContactSelected"
    >
      <option value="">
        Select contact
      </option>
      <option
        v-for="contact in allContacts"
        :key="contact.email"
        :value="contact.email"
      >
        {{ contact.name }}
      </option>
    </select>

    <input
      ref="nameInputRef"
      v-model="nameInput"
      type="text"
      :name="props.inputNameName"
      class="input input-sm input-bordered w-full mt-1"
      placeholder="Name"
      data-email-input="true"
      required
      @input="checkForDuplicateSubmitter"
      @blur="validateInputs"
    >

    <input
      ref="emailInputRef"
      v-model="emailInput"
      type="text"
      :name="props.inputNameEmail"
      class="input input-sm input-bordered w-full mt-1"
      placeholder="Email"
      data-email-input="true"
      required
      @input="checkForDuplicateSubmitter"
      @blur="validateInputs"
    >
    <!-- Only show warning for BCC recipients -->
    <p
      v-if="isDuplicateSubmitter && showWarning"
      class="text-xs text-amber-600 mt-1"
    >
      Signers will receive a copy upon completion; no need to add them to {{ props.inputNameEmail?.includes('real_bcc_recipients') ? 'BCC' : 'CC' }}
    </p>

    <!-- Warning input validation -->
    <div
      v-if="inputBccErrors.length"
      class="text-red-500 text-sm mt-1"
    >
      <div
        v-for="(err, idx) in inputBccErrors"
        :key="idx"
      >
        {{ err }}
      </div>
    </div>

    <!-- Existing Contact Modal -->
    <div
      id="contactModal"
      :class="`modal fade ${isModalOpen && 'modal-open'} items-start !animate-none overflow-y-auto`"
    >
      <div
        class="absolute top-0 bottom-0 right-0 left-0"
        @click.self="isModalOpen = false"
      >
        <div class="modal-box pt-4 pb-6 px-6 mt-12 max-h-none w-full max-w-xl">
          <!-- Modal Content Form -->
          <div class="modal-content-form">
            <div class="flex justify-between items-center border-b pb-2 mb-2 font-medium">
              <span>Select or Add Contact</span>
              <a
                href="#"
                class="text-xl"
                @click.prevent="isModalOpen = false"
              >&times;</a>
            </div>

            <form
              class="mb-6"
              @submit.prevent="onSubmitNewContact"
            >
              <div class="mb-4">
                <label class="label">
                  <span class="label-text">Name</span>
                </label>
                <input
                  v-model="newContact.name"
                  type="text"
                  class="input input-sm input-bordered w-full"
                  required
                >
                <div
                  v-if="inputContactErrors.name"
                  class="text-red-500 text-sm mt-1"
                >
                  {{ inputContactErrors.name }}
                </div>
              </div>

              <div class="mb-4">
                <label class="label">
                  <span class="label-text">Email</span>
                </label>
                <input
                  v-model="newContact.email"
                  type="email"
                  class="input input-sm input-bordered w-full"
                  required
                >
                <div
                  v-if="inputContactErrors.email"
                  class="text-red-500 text-sm mt-1"
                >
                  {{ inputContactErrors.email }}
                </div>
              </div>

              <div class="flex justify-end space-x-2">
                <button
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  @click="isModalOpen = false"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </form>
          </div>

          <!-- Modal Content Contact List -->
          <div class="modal-content-contact-list">
            <div class="flex items-center justify-between mb-2 border-b pb-1">
              <h2 class="text-lg font-semibold text-gray-800">
                Edit Existing Contacts
              </h2>
              <button
                class="btn btn-sm ml-2
                      bg-purple-800 text-white
                      hover:bg-purple-900
                      rounded-md
                      px-3 py-1
                      transition-colors duration-300
                      flex items-center gap-1
                      normal-case group"
                @click="refreshLocalContacts"
              >
                <IconRefresh
                  size="18"
                  class="opacity-70 transition-transform duration-300 group-hover:rotate-180"
                />
                Refresh
              </button>
            </div>
            <input
              v-model="searchKey"
              type="text"
              class="input input-sm input-bordered w-full mb-2"
              placeholder="Search"
            >
            <div class="relative overflow-x-auto rounded">
              <table class="w-full text-sm text-left rtl:text-right">
                <thead class="text-xs text-gray-700 uppercase bg-base-200">
                  <tr>
                    <th
                      scope="col"
                      class="px-6 py-3"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3"
                    >
                      Email
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(contact, index) in allContacts"
                    :key="`contact-${index}`"
                    class="border-b hover:cursor-pointer hover:bg-base-300"
                    @click.stop="onSelectedContact(contact, $event)"
                  >
                    <td class="px-6 py-4">
                      <input
                        v-model="contact.name"
                        class="input input-sm w-full"
                        @blur="updateBccContact(index, contact)"
                        @click.stop
                      >
                    </td>
                    <td class="px-6 py-4">
                      <input
                        v-model="contact.email"
                        class="input input-sm w-full"
                        @blur="updateBccContact(index, contact)"
                        @click.stop
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { debounceFilter, watchWithFilter } from '@vueuse/core'
import { IconAddressBook, IconRefresh } from '@tabler/icons-vue'
import { defineProps, ref, watch, onMounted, onUnmounted, nextTick, computed } from 'vue'

const props = defineProps(['title', 'input-name-name', 'inputNameEmail', 'email'])

const emailInput = ref('')
const nameInput = ref('')
const searchKey = ref('')
const isModalOpen = ref(false)
const allContacts = ref([])
let checkTimeoutId = null
const isDuplicateSubmitter = ref(false)
const isDuplicateName = ref(false)
const nameInputRef = ref(null)
const emailInputRef = ref(null)
const bccSelectedContact = ref('')
const LOCAL_CONTACT_KEY = 'local-contacts'
const newContact = ref({
  name: '',
  email: ''
})
const inputContactErrors = ref({
  name: '',
  email: ''
})
const inputBccErrors = ref([])

// Computed property to determine if we should show the warning
// Only show for BCC recipient inputs
const showWarning = computed(() => {
  return props.inputNameEmail?.includes('bcc_recipients')
})

// Computed property to determine if this is the main contact form
const isMainContactForm = computed(() => {
  return props.inputNameEmail === 'emails' || props.title === 'Contact' || props.inputNameEmail?.includes('submitters ') || props.title?.includes('Party')
})

const handleEsc = (event) => {
  if (event.key === 'Escape') {
    isModalOpen.value = false
  }
}

// Function to open contact book modal and load contacts
const openContactBook = () => {
  isModalOpen.value = !isModalOpen.value

  // If opening the modal, fetch contacts
  if (isModalOpen.value) {
    // If we have an email, use it as initial search
    if (emailInput.value) {
      searchKey.value = emailInput.value
      getAllContacts(emailInput.value)
    }
  }
}

// Function to add the current user's information to the form
const addMeToSign = async () => {
  try {
    // Fetch the current user's information
    const response = await fetch('/api/current_user_info.json')

    if (response.ok) {
      const userData = await response.json()

      // Fill the form with the current user's data
      if (userData && userData.email) {
        emailInput.value = userData.email
        nameInput.value = userData.name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim()

        // Check for duplicates after updating
        checkForDuplicateSubmitter()
      }
    } else {
      console.error('Failed to fetch current user info')
    }
  } catch (error) {
    console.error('Error fetching current user info:', error)
  }
}

// Special debugging function to list all email inputs on the page
const logAllEmailInputs = () => {
  console.log('-----DEBUGGING EMAIL INPUTS-----')
  const allInputs = document.querySelectorAll('input[name*="email"]')
  console.log(`Found ${allInputs.length} email inputs:`)

  allInputs.forEach((input, index) => {
    console.log(`${index + 1}. Name: ${input.name}, Value: ${input.value}, ID: ${input.id}`)
  })
}

// Method to force checking all components
const forceCheckAll = () => {
  // Find all submitter autocomplete components
  const components = document.querySelectorAll('new-submitter-autocomplete')
  console.log(`Found ${components.length} submitter components`)

  // Trigger check on each input
  document.querySelectorAll('input[data-email-input="true"]').forEach(input => {
    // Create and dispatch an input event
    const event = new Event('input', { bubbles: true })
    input.dispatchEvent(event)
  })
}

onMounted(() => {
  window.addEventListener('keydown', handleEsc)

  // Auto-fill fields if email prop is provided
  if (props.email) {
    emailInput.value = props.email
    // Fetch contact data for this email
    fetchContactByEmail(props.email)
  }

  getAllContacts('')

  // Add initial debugging information
  console.log('Component mounted with inputNameEmail:', props.inputNameEmail)

  // Wait for all components to be mounted
  setTimeout(() => {
    // Log all email inputs for debugging
    logAllEmailInputs()

    // Run initial check
    checkForDuplicateSubmitter()

    // Set up a mutation observer to detect changes to the form
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          if (checkTimeoutId) clearTimeout(checkTimeoutId)
          checkTimeoutId = setTimeout(() => {
            forceCheckAll()
          }, 300)
          break
        }
      }
    })

    // Start observing the form
    const form = document.querySelector('form')
    if (form) {
      observer.observe(form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value']
      })
    }

    // Return cleanup function
    return () => {
      if (observer) observer.disconnect()
    }
  }, 500)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleEsc)
  if (checkTimeoutId) {
    clearTimeout(checkTimeoutId)
  }
})

// Watch for changes to the email input
watch(emailInput, () => {
  if (checkTimeoutId) clearTimeout(checkTimeoutId)
  checkTimeoutId = setTimeout(() => {
    checkForDuplicateSubmitter()
  }, 200)
})

watch(nameInput, () => {
  if (checkTimeoutId) clearTimeout(checkTimeoutId)

  checkTimeoutId = setTimeout(async () => {
    const contactExist = await checkDuplicateContacts(nameInput.value)
    isDuplicateName.value = contactExist
    inputBccErrors.value = contactExist ? ['Nama ini sudah digunakan di kontak lain.'] : []
  }, 700)
})

watch(isModalOpen, (newVal, oldVal) => {
  if (newVal && !oldVal && allContacts.value.length === 0) {
    // Get all contacts when opening the modal if none are loaded
    getAllContacts('')
  }
})

watchWithFilter(
  searchKey,
  () => {
    getAllContacts(searchKey.value)
  },
  {
    eventFilter: debounceFilter(500, { maxWait: 1000 })
  }
)

// Used for both general search and to load all contacts
const getAllContacts = async (text) => {
  const q = text.split(/[;,]+/).pop().trim()
  const queryParams = new URLSearchParams({ q })

  const keyword = searchKey.value?.trim().toLowerCase() || ''
  const localData = localStorage.getItem(LOCAL_CONTACT_KEY)
  const localParsed = localData ? JSON.parse(localData) : []

  try {
    const resp = await fetch('/submitters_autocomplete?' + queryParams)
    const fetched = await resp.json()

    // --- Merge fetched data ke local data ---
    const merged = [...localParsed]

    for (const contact of fetched) {
      const exists = merged.find(c => c.email?.toLowerCase() === contact.email?.toLowerCase())
      if (!exists) merged.push(contact)
    }

    // Update hasil pencarian (filter)
    const filtered = keyword
      ? merged.filter(c =>
        (c.email && c.email.toLowerCase().includes(keyword)) ||
          (c.name && c.name.toLowerCase().includes(keyword))
      )
      : merged

    allContacts.value = filtered

    // Simpan hasil merge ke localStorage
    localStorage.setItem(LOCAL_CONTACT_KEY, JSON.stringify(merged))
  } catch (error) {
    console.error('Error fetching contacts:', error)

    // Fallback ke data lokal aja kalau gagal
    const filtered = keyword
      ? localParsed.filter(c =>
        (c.email && c.email.toLowerCase().includes(keyword)) ||
          (c.name && c.name.toLowerCase().includes(keyword))
      )
      : localParsed

    allContacts.value = filtered
  }
}

// Used specifically to load contact details when email is provided as a prop
const fetchContactByEmail = (email) => {
  if (!email) return

  console.log('Fetching contact by email:', email)

  try {
    const normalizedEmail = email.trim().toLowerCase()

    const matched = allContacts.value.find(contact =>
      contact.email && contact.email.toLowerCase() === normalizedEmail
    )

    if (matched) {
      emailInput.value = matched.email || ''
    } else {
      emailInput.value = '' // reset jika tidak ditemukan
    }
  } catch (err) {
    console.error('Failed to fetch contact from localStorage:', err)
  }
}

const onSelectedContact = (contact, event) => {
  if (event.type === 'click') isModalOpen.value = false

  event.stopPropagation()

  nameInput.value = contact.name || ''
  emailInput.value = contact.email || ''

  bccSelectedContact.value = contact?.email || ''
}

const checkForDuplicateSubmitter = () => {
  console.log('Checking for duplicate submitter')

  // Reset detection state
  isDuplicateSubmitter.value = false

  // Skip if no email is entered
  if (!emailInput.value || emailInput.value.trim() === '') {
    console.log('No email value, skipping check')
    return
  }

  const normalizedEmail = emailInput.value.trim().toLowerCase()
  console.log('Current email:', normalizedEmail)

  // Special case handling for emails/names fields (first form)
  // In this case we need additional checks depending on where the component is
  const isSimpleForm = props.inputNameEmail === 'emails'
  const isPrimarySubmitter = isSimpleForm || props.inputNameEmail?.includes('submitters')
  const isBccRecipient = props.inputNameEmail?.includes('bcc_recipients')

  console.log('Form fields:', { isSimpleForm, isPrimarySubmitter, isBccRecipient })

  // Get all inputs with email in the name attribute
  const allEmailInputs = Array.from(document.querySelectorAll('input[name*="email"]'))
  console.log('Found email inputs:', allEmailInputs.length)

  // Get the current input element to exclude it from the check
  const currentInput = emailInputRef.value || document.querySelector(`input[name="${props.inputNameEmail}"]`)
  console.log('Current input found:', !!currentInput)

  // Find specific duplicates between submitter and BCC
  let duplicateFound = false

  if (isPrimarySubmitter) {
    // If current is a submitter, look for matching BCC
    const bccInputs = allEmailInputs.filter(input =>
      input !== currentInput &&
      input.name?.includes('bcc_recipients') &&
      input.value?.trim().toLowerCase() === normalizedEmail
    )
    duplicateFound = bccInputs.length > 0
    if (duplicateFound) {
      console.log('Found duplicate in BCC recipients:', bccInputs.map(i => i.name))
    }
  } else if (isBccRecipient) {
    // If current is a BCC, look for matching submitter
    const submitterInputs = allEmailInputs.filter(input =>
      input !== currentInput &&
      (input.name === 'emails' || input.name?.includes('submitters')) &&
      input.value?.trim().toLowerCase() === normalizedEmail
    )
    duplicateFound = submitterInputs.length > 0
    if (duplicateFound) {
      console.log('Found duplicate in submitters:', submitterInputs.map(i => i.name))
    }
  }

  // Handle the case from screenshot - check specifically for the pattern
  // where main contact field (name="emails") matches a BCC recipient
  if (!duplicateFound && isSimpleForm) {
    const bccInputs = document.querySelectorAll('input[name*="bcc_recipients"][name*="email"]')
    for (const bccInput of bccInputs) {
      const bccEmail = bccInput.value?.trim().toLowerCase()
      if (bccEmail && bccEmail === normalizedEmail) {
        duplicateFound = true
        console.log('Found duplicate from simple form to BCC:', bccInput.name)
        break
      }
    }
  } else if (!duplicateFound && isBccRecipient) {
    // Check if any "emails" field matches this BCC
    const primaryInputs = document.querySelectorAll('input[name="emails"]')
    for (const primaryInput of primaryInputs) {
      const primaryEmail = primaryInput.value?.trim().toLowerCase()
      if (primaryEmail && primaryEmail === normalizedEmail) {
        duplicateFound = true
        console.log('Found duplicate from BCC to simple form email field')
        break
      }
    }
  }

  // Update the warning flag
  isDuplicateSubmitter.value = duplicateFound
  console.log('isDuplicateSubmitter set to:', isDuplicateSubmitter.value)
}

// Function to check duplicate name in contacts
const checkDuplicateContacts = async (name) => {
  const normalizedName = name?.trim().toLowerCase()
  try {
    await getAllContacts(name)
    return allContacts.value.some(contact => contact.name?.trim().toLowerCase() === normalizedName)
  } catch (e) {
    console.error('Error fetching contacts', e)
    return false
  }
}

// Function to validate the inputs
const validateInputs = () => {
  const errors = []

  if (!nameInput.value.trim()) {
    errors.push('Name is required.')
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailPattern.test(emailInput.value.trim())) {
    errors.push('Email is required.')
  }

  // Check for duplicates name
  const isDuplicateName = allContacts.value.some(contact =>
    contact.name.toLowerCase() === nameInput.value.toLowerCase()
  )

  // Check for duplicate email
  const isDuplicateEmail = allContacts.value.some(contact =>
    contact.email.toLowerCase() === emailInput.value.toLowerCase()
  )

  if (isDuplicateEmail) {
    errors.push('Email has been used in other fields.')
  }

  if (isDuplicateName) {
    errors.push('Name has been used in other fields.')
  }

  inputBccErrors.value = errors
  return errors.length === 0
}

const updateBccContact = (index, updatedContact) => {
  // Optional: validasi name/email tidak kosong
  if (!updatedContact.name.trim() || !updatedContact.email.trim()) {
    alert('Name dan Email tidak boleh kosong.')
    return
  }

  // Bisa langsung update karena objeknya reactive
  allContacts.value[index] = { ...updatedContact }
  console.log('Contact updated:', allContacts.value[index])
}

const onBccContactSelected = () => {
  const selectedContact = allContacts.value.find(c => c.email === bccSelectedContact.value)
  if (selectedContact) {
    nameInput.value = selectedContact.name
    emailInput.value = selectedContact.email
  }
}

const onSubmitNewContact = () => {
  // Reset previous errors
  inputContactErrors.value = {
    name: '',
    email: ''
  }

  // Basic validation
  let hasError = false
  if (!newContact.value.name) {
    inputContactErrors.value.name = 'Name is required'
    hasError = true
  }

  if (!newContact.value.email) {
    inputContactErrors.value.email = 'Email is required'
    hasError = true
  }

  if (hasError) return

  // Ambil semua kontak dari localStorage (bukan hanya yang tampil)
  const stored = JSON.parse(localStorage.getItem(LOCAL_CONTACT_KEY) || '[]')
  const nameLower = newContact.value.name.trim().toLowerCase()
  const emailLower = newContact.value.email.trim().toLowerCase()

  // Check for duplicates
  const isDuplicateName = stored.some(contact =>
    contact.name?.toLowerCase() === nameLower
  )
  const isDuplicateEmail = stored.some(contact =>
    contact.email?.toLowerCase() === emailLower
  )

  if (isDuplicateName) {
    inputContactErrors.value.name = 'Name already exists'
  }

  if (isDuplicateEmail) {
    inputContactErrors.value.email = 'Email already exists'
  }

  if (isDuplicateName || isDuplicateEmail) return

  // Tambah ke storage
  stored.push({ ...newContact.value })

  // Simpan ke localStorage
  localStorage.setItem(LOCAL_CONTACT_KEY, JSON.stringify(stored))

  // Update tampilan sesuai search
  const keyword = searchKey.value?.trim().toLowerCase() || ''
  allContacts.value = keyword
    ? stored.filter(c =>
      (c.email && c.email.toLowerCase().includes(keyword)) ||
        (c.name && c.name.toLowerCase().includes(keyword))
    )
    : stored

  // Reset form
  newContact.value = { name: '', email: '' }
}
const refreshLocalContacts = async () => {
  const confirmed = window.confirm('Yakin ingin menghapus semua kontak yang tersimpan di browser?')

  if (confirmed) {
    try {
      const queryParams = new URLSearchParams({ q: '' }) // ambil semua
      const resp = await fetch('/submitters_autocomplete?' + queryParams)
      const items = await resp.json()

      // Simpan ulang ke localStorage
      localStorage.setItem(LOCAL_CONTACT_KEY, JSON.stringify(items))

      // Update tampilan juga
      const keyword = searchKey.value?.trim().toLowerCase() || ''
      allContacts.value = keyword
        ? items.filter(c =>
          (c.email && c.email.toLowerCase().includes(keyword)) ||
          (c.name && c.name.toLowerCase().includes(keyword))
        )
        : items

      console.log('Contacts refreshed from server.')
    } catch (error) {
      console.error('Failed to refresh contacts:', error)
    }
  }
}
</script>
